import { useEffect, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  getStatusBreakdown,
  type StatusBreakdownResult,
} from "../../api/dashboardApi";
import { showError } from "../../utils/ alert";

type DocType = "quotation" | "invoice" | "delivery_order";

interface StatusDocumentSectionProps {
  startDate: string;
  endDate: string;
}

const DOC_TABS: { key: DocType; label: string }[] = [
  { key: "quotation", label: "Quotation" },
  { key: "invoice", label: "Invoice" },
  { key: "delivery_order", label: "DO" },
];



export default function StatusDocumentSection({
  startDate,
  endDate,
}: StatusDocumentSectionProps) {
  const [activeTab, setActiveTab] = useState<DocType>("quotation");
  const [results, setResults] = useState<
    Record<DocType, StatusBreakdownResult | null>
  >({
    quotation: null,
    invoice: null,
    delivery_order: null,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);

        const [quotation, invoice, delivery_order] = await Promise.all([
          getStatusBreakdown("quotation", startDate, endDate),
          getStatusBreakdown("invoice", startDate, endDate),
          getStatusBreakdown("delivery_order", startDate, endDate),
        ]);

        setResults({ quotation, invoice, delivery_order });
      } catch (error) {
       showError("Failed", "Failed to Load invoice.");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [startDate, endDate]);

  // Bangun data buat stacked bar: 1 baris per doc type, kolom = tiap status
  const allStatuses = Array.from(
    new Set(
      Object.values(results)
        .filter(Boolean)
        .flatMap((r) => r!.breakdown.map((b) => b.name))
    )
  );

  const chartData = [
    { name: "Quotation", ...toStatusMap(results.quotation) },
    { name: "Invoice", ...toStatusMap(results.invoice) },
    { name: "DO", ...toStatusMap(results.delivery_order) },
  ];

  const activeResult = results[activeTab];

  const statusColorMap = Object.fromEntries(
    Object.values(results)
      .filter(Boolean)
      .flatMap((r) => r!.breakdown.map((b) => [b.name, b.color]))
  );

  return (
    <div className="rounded-2xl bg-white p-6 shadow-sm">
      <h3 className="mb-5 font-semibold text-[#324A5A]">Status Dokumen</h3>

      <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
        {/* Stacked bar chart */}
        <div>
          {loading ? (
            <p className="py-10 text-center text-sm text-slate-400">
              Loading...
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={chartData}
                margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#F1F5F9"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#94A3B8" }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: "#94A3B8" }}
                />
                <Tooltip />
                {allStatuses.map((status) => (
                  <Bar
                    key={status}
                    dataKey={status}
                    stackId="a"
                    fill={statusColorMap[status] ?? "#CBD5E1"}
                    radius={
                      status === allStatuses[allStatuses.length - 1]
                        ? [6, 6, 0, 0]
                        : undefined
                    }
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Tab + legend table */}
        <div>
          <div className="flex gap-2">
            {DOC_TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 rounded-xl px-3 py-2 text-sm font-semibold transition ${
                  activeTab === tab.key
                    ? "bg-[#3DA4A8] text-white"
                    : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                }`}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4 overflow-hidden rounded-xl border border-slate-100">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#3DA4A8] text-white">
                  <th className="px-4 py-2.5 text-left font-medium">
                    Status Document
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium">Jumlah</th>
                </tr>
              </thead>
              <tbody>
                {activeResult?.breakdown.map((item) => (
                  <tr key={item.code} className="border-t border-slate-100">
                    <td className="flex items-center gap-2 px-4 py-2.5 text-slate-600">
                      <span
                        className="h-2.5 w-2.5 rounded-full"
                        style={{
                          backgroundColor: item.color ?? "#CBD5E1",
                        }}
                      />
                      {item.name}
                    </td>

                    <td className="px-4 py-2.5 text-right font-medium text-[#324A5A]">
                      {item.count}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-slate-100 bg-[#3DA4A8]/90 text-white">
                  <td className="px-4 py-2.5 font-semibold">TOTAL</td>
                  <td className="px-4 py-2.5 text-right font-semibold">
                    {activeResult?.total ?? 0}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function toStatusMap(result: StatusBreakdownResult | null) {
  if (!result) return {};

  return Object.fromEntries(result.breakdown.map((b) => [b.name, b.count]));
}


